import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileSpreadsheet,
  RefreshCw,
} from "lucide-react";
import * as XLSX from "xlsx";

import { ClientDebtDropdown } from "~/components/debt/ClientDebtDropdown";
import { CutoffDateSelector } from "~/components/debt/CutoffDateSelector";
import { DebtSummaryCards } from "~/components/debt/DebtSummaryCards";
import { DebtTabSelector, DebtType } from "~/components/debt/DebtTabSelector";
import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useClientDebtDetails,
  useDebtSummary,
  useInvalidateDebtQueries,
  useManualFacturasDebt,
  useSalesRequestsDebt,
} from "~/hooks/useNewDebtSummary";

export default function DeudasPage() {
  // State management
  const [activeTab, setActiveTab] = useState<DebtType>("sales_requests");
  const [cutoffDate, setCutoffDate] = useState<Date | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [minDebtAmount, setMinDebtAmount] = useState<string>("");
  const [maxDebtAmount, setMaxDebtAmount] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<
    "client_name" | "client_rut" | "total_debt"
  >("total_debt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const clientsPerPage = 10;

  // Data fetching
  const {
    data: debtSummary = [],
    isLoading: summaryLoading,
    error: summaryError,
  } = useDebtSummary(cutoffDate);
  const {
    data: clientDetails = [],
    isLoading: clientsLoading,
    error: clientsError,
  } = useClientDebtDetails(cutoffDate);

  // Fetch complete debt data for export
  const { data: allSalesRequestsDebt = [] } = useSalesRequestsDebt(cutoffDate);
  const { data: allManualFacturasDebt = [] } =
    useManualFacturasDebt(cutoffDate);

  const { invalidateAllDebtQueries } = useInvalidateDebtQueries();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchQuery,
    activeTab,
    minDebtAmount,
    maxDebtAmount,
    sortField,
    sortDirection,
  ]);

  // Sort handler
  const handleSort = (field: "client_name" | "client_rut" | "total_debt") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Render sortable header
  const renderSortableHeader = (
    field: "client_name" | "client_rut" | "total_debt",
    label: string,
    className: string = ""
  ) => {
    const isActive = sortField === field;
    const isAsc = sortDirection === "asc";

    return (
      <TableHead
        className={`cursor-pointer hover:bg-gray-50 ${className}`}
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            isAsc ? (
              <ArrowUp className="h-4 w-4" />
            ) : (
              <ArrowDown className="h-4 w-4" />
            )
          ) : (
            <ArrowUpDown className="h-4 w-4 opacity-50" />
          )}
        </div>
      </TableHead>
    );
  };

  // Excel export function - similar to DeudasPage but filtered by active tab
  const handleExportToExcel = async () => {
    try {
      const exportData = [];

      if (activeTab === "sales_requests") {
        // Export sales requests debt, only positive debt
        allSalesRequestsDebt
          .filter((request) => request.debt_amount_at_cutoff > 0)
          .forEach((request) => {
            const emissionDate = dayjs(request.factura_emission_date);
            const dueDate = dayjs(request.payment_due_date);
            // Use days_to_due_at_cutoff from DB directly to match UI vencida classification
            const daysOverdue =
              request.days_to_due_at_cutoff < 0
                ? Math.abs(request.days_to_due_at_cutoff)
                : 0;

            exportData.push({
              tipo_documento: "Factura (de Nota de Venta)",
              factura: request.factura_number,
              rut_cliente: request.client_rut,
              vendedor: request.assigned_seller,
              cliente: request.client_name,
              fecha_emision: emissionDate.format("DD/MM/YYYY"),
              fecha_vencimiento: dueDate.format("DD/MM/YYYY"),
              monto_total_adeudado: request.original_amount,
              monto: request.debt_amount_at_cutoff,
              condicion_de_venta: request.sales_condition_name,
              dias_vencidos: daysOverdue,
              estado:
                request.days_to_due_at_cutoff < 0
                  ? request.cobranza_judicial
                    ? "Cobranza Judicial"
                    : request.siniestro
                      ? "Siniestro"
                      : "Vencido"
                  : "Por vencer",
              cheque_en_cartera: request.has_checks_in_portfolio
                ? request.checks_in_portfolio_amount
                : 0,
            });
          });
      } else {
        // Export manual facturas debt, only positive debt
        allManualFacturasDebt
          .filter((factura) => factura.debt_amount_at_cutoff > 0)
          .forEach((factura) => {
            const creationDate = dayjs(factura.created_at);
            const dueDate = dayjs(factura.payment_due_date);
            // Use days_to_due_at_cutoff from DB directly to match UI vencida classification
            const daysOverdue =
              factura.days_to_due_at_cutoff < 0
                ? Math.abs(factura.days_to_due_at_cutoff)
                : 0;

            exportData.push({
              tipo_documento: "Factura Manual",
              factura: factura.doc_number,
              rut_cliente: factura.client_rut,
              vendedor: factura.assigned_seller,
              cliente: factura.client_name,
              fecha_emision: creationDate.format("DD/MM/YYYY"),
              fecha_vencimiento: dueDate.format("DD/MM/YYYY"),
              monto_total_adeudado: factura.original_balance,
              monto: factura.debt_amount_at_cutoff,
              condicion_de_venta: "Factura Manual",
              dias_vencidos: daysOverdue,
              estado:
                factura.days_to_due_at_cutoff < 0
                  ? factura.cobranza_judicial
                    ? "Cobranza Judicial"
                    : factura.siniestro
                      ? "Siniestro"
                      : "Vencido"
                  : "Por vencer",
              cheque_en_cartera: 0, // Manual facturas don't currently track check portfolio
            });
          });
      }

      // Restore previous ordering: within each client, overdue invoices
      // ("Vencido" / past-due) sort to the bottom. Client groups keep their
      // source order (by client name) and the relative order inside each group
      // is preserved thanks to Array.prototype.sort being stable.
      const clientOrder = new Map<string, number>();
      exportData.forEach((item) => {
        if (!clientOrder.has(item.cliente)) {
          clientOrder.set(item.cliente, clientOrder.size);
        }
      });
      exportData.sort((a, b) => {
        const clientCmp =
          (clientOrder.get(a.cliente) ?? 0) - (clientOrder.get(b.cliente) ?? 0);
        if (clientCmp !== 0) return clientCmp;
        const aOverdue = a.dias_vencidos > 0 ? 1 : 0;
        const bOverdue = b.dias_vencidos > 0 ? 1 : 0;
        return aOverdue - bOverdue;
      });

      // Create worksheet with Spanish headers (same format as DeudasPage)
      const worksheetData = exportData.map((item) => ({
        "Tipo de Documento": item.tipo_documento,
        Factura: item.factura,
        "RUT Cliente": item.rut_cliente,
        Vendedor: item.vendedor,
        Cliente: item.cliente,
        "Fecha Emisión": item.fecha_emision,
        "Fecha Vencimiento": item.fecha_vencimiento,
        "Monto Total Adeudado": item.monto_total_adeudado,
        "Monto Pendiente": item.monto,
        "Condición de Venta": item.condicion_de_venta,
        "Días Vencidos": item.dias_vencidos,
        Estado: item.estado,
        "Cheque en Cartera": item.cheque_en_cartera,
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);

      // Set column widths (same as DeudasPage)
      const columnWidths = [
        { wch: 25 }, // Tipo de Documento
        { wch: 18 }, // Factura
        { wch: 15 }, // RUT Cliente
        { wch: 25 }, // Vendedor
        { wch: 35 }, // Cliente
        { wch: 15 }, // Fecha Emisión
        { wch: 18 }, // Fecha Vencimiento
        { wch: 20 }, // Monto Total Adeudado
        { wch: 18 }, // Monto Pendiente
        { wch: 22 }, // Condición de Venta
        { wch: 15 }, // Días Vencidos
        { wch: 18 }, // Estado
        { wch: 18 }, // Cheque en Cartera
      ];
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Deudas");

      // Generate filename with tab type and current date
      const tabName =
        activeTab === "sales_requests" ? "notas_venta" : "facturas_manuales";
      const filename = `deudas_${tabName}_${dayjs().format("YYYY-MM-DD")}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    }
  };

  // Process summary data - show debt filtered by active tab
  const processedSummary = useMemo(() => {
    const salesRequestsSummary = debtSummary.find(
      (s) => s.debt_type === "sales_requests"
    );
    const manualFacturasSummary = debtSummary.find(
      (s) => s.debt_type === "manual_facturas"
    );

    if (activeTab === "sales_requests") {
      return {
        totalDebt: salesRequestsSummary?.total_debt || 0,
        prontoVencer: salesRequestsSummary?.pronto_vencer || 0,
        sana: salesRequestsSummary?.sana || 0,
        vencida: salesRequestsSummary?.vencida || 0,
      };
    } else {
      return {
        totalDebt: manualFacturasSummary?.total_debt || 0,
        prontoVencer: manualFacturasSummary?.pronto_vencer || 0,
        sana: manualFacturasSummary?.sana || 0,
        vencida: manualFacturasSummary?.vencida || 0,
      };
    }
  }, [debtSummary, activeTab]);

  // Filter clients by search query, active tab, and price range
  const filteredClients = useMemo(() => {
    let filtered = clientDetails.filter((client) => {
      const matchesSearch =
        !debouncedSearchQuery ||
        client.client_name
          .toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase()) ||
        client.client_rut
          .toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase()) ||
        (client.client_email &&
          client.client_email
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()));

      const hasRelevantDebt =
        activeTab === "sales_requests"
          ? client.sales_requests_debt > 0
          : client.manual_facturas_debt > 0;

      // Price range filter based on the active tab's debt amount
      const debtAmount =
        activeTab === "sales_requests"
          ? client.sales_requests_debt
          : client.manual_facturas_debt;

      const matchesMinDebt =
        !minDebtAmount || debtAmount >= parseFloat(minDebtAmount);
      const matchesMaxDebt =
        !maxDebtAmount || debtAmount <= parseFloat(maxDebtAmount);

      return (
        matchesSearch && hasRelevantDebt && matchesMinDebt && matchesMaxDebt
      );
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "client_name":
          comparison = a.client_name.localeCompare(b.client_name);
          break;
        case "client_rut":
          comparison = a.client_rut.localeCompare(b.client_rut);
          break;
        case "total_debt":
          comparison = a.total_debt - b.total_debt;
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [
    clientDetails,
    debouncedSearchQuery,
    activeTab,
    minDebtAmount,
    maxDebtAmount,
    sortField,
    sortDirection,
  ]);

  // Paginate filtered clients
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * clientsPerPage;
    const endIndex = startIndex + clientsPerPage;
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, currentPage, clientsPerPage]);

  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  // Handle refresh
  const handleRefresh = () => {
    invalidateAllDebtQueries();
  };

  // Loading state
  if (summaryLoading || clientsLoading) {
    return (
      <PageContainer>
        <Card className="w-full rounded-lg">
          <CardHeader>
            <CardTitle>Deudas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Cards Loading */}
            <DebtSummaryCards
              totalDebt={0}
              prontoVencer={0}
              sana={0}
              vencida={0}
              loading={true}
            />

            {/* Tab Selector Loading */}
            <Skeleton className="h-10 w-full" />

            {/* Client Details Loading */}
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  // Error state
  if (summaryError || clientsError) {
    return (
      <PageContainer>
        <Card className="rounded-lg">
          <CardHeader>
            <CardTitle>Deudas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
              <p className="font-medium text-red-700">
                Error al cargar los datos
              </p>
              <p className="mt-1 text-sm text-red-600">
                {(summaryError as Error)?.message ||
                  (clientsError as Error)?.message ||
                  "Error desconocido"}
              </p>
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="mt-3"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Deudas"
        description="Gestión optimizada de deudas con cálculos históricos"
        actions={
          <>
            <Button
              onClick={handleExportToExcel}
              variant="default"
              size="sm"
              className="h-9"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Exportar Excel
            </Button>
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              className="h-9"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Actualizar
            </Button>
          </>
        }
      />

      {cutoffDate && (
        <p className="text-sm text-blue-600">
          Mostrando deuda histórica al {dayjs(cutoffDate).format("DD/MM/YYYY")}
        </p>
      )}

      {/* Tab Selector */}
      <DebtTabSelector activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Debt Summary */}
      <DebtSummaryCards
        totalDebt={processedSummary.totalDebt}
        prontoVencer={processedSummary.prontoVencer}
        sana={processedSummary.sana}
        vencida={processedSummary.vencida}
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* Search */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                Buscar Cliente
              </label>
              <Input
                placeholder="Nombre, RUT o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full"
              />
            </div>

            {/* Cutoff Date Selector */}
            <CutoffDateSelector
              cutoffDate={cutoffDate}
              onCutoffDateChange={setCutoffDate}
            />

            {/* Price Range Filter */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                Rango de Monto
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Mínimo"
                  value={minDebtAmount}
                  onChange={(e) => setMinDebtAmount(e.target.value)}
                  className="h-9 w-full"
                />
                <span className="shrink-0 text-gray-400">a</span>
                <Input
                  type="number"
                  placeholder="Máximo"
                  value={maxDebtAmount}
                  onChange={(e) => setMaxDebtAmount(e.target.value)}
                  className="h-9 w-full"
                />
              </div>
            </div>

            {/* Clear Filters */}
            {(minDebtAmount || maxDebtAmount || searchQuery || cutoffDate) && (
              <div className="flex items-end sm:col-span-2 lg:col-span-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setMinDebtAmount("");
                    setMaxDebtAmount("");
                    setSearchQuery("");
                    setCutoffDate(undefined);
                  }}
                  className="h-9 text-gray-500 hover:text-gray-700"
                >
                  Limpiar filtros
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Client Details */}
      <div className="space-y-4">
        {filteredClients.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <p className="font-medium text-gray-600">
              No se encontraron clientes
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery
                ? `No hay clientes que coincidan con "${searchQuery}" para el tipo de deuda seleccionado`
                : `No hay clientes con ${activeTab === "sales_requests" ? "notas de venta" : "facturas manuales"} pendientes`}
              {cutoffDate && " en la fecha seleccionada"}
            </p>
            {searchQuery && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearchQuery("")}
                className="mt-3"
              >
                Limpiar búsqueda
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Mostrando {paginatedClients.length} de {filteredClients.length}{" "}
                cliente
                {filteredClients.length !== 1 ? "s" : ""} con deuda
                {activeTab === "sales_requests"
                  ? " en notas de venta"
                  : " en facturas manuales"}
              </p>
            </div>

            <div className="overflow-hidden rounded-md border">
              <div className="w-full">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {renderSortableHeader(
                        "client_name",
                        "Cliente",
                        "w-[45%]"
                      )}
                      {renderSortableHeader("client_rut", "RUT", "w-[25%]")}
                      {renderSortableHeader(
                        "total_debt",
                        "Total Deuda",
                        "w-[30%]"
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedClients.map((client, index) => (
                      <ClientDebtDropdown
                        key={client.client_id}
                        client={client}
                        activeTab={activeTab}
                        cutoffDate={cutoffDate}
                        index={index}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                    Primera
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="flex items-center gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                </div>

                {/* Numbered Page Buttons */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const getPageNumbers = () => {
                      const delta = 2;
                      const range = [];
                      const rangeWithDots = [];

                      for (
                        let i = Math.max(2, currentPage - delta);
                        i <= Math.min(totalPages - 1, currentPage + delta);
                        i++
                      ) {
                        range.push(i);
                      }

                      if (currentPage - delta > 2) {
                        rangeWithDots.push(1, "...");
                      } else {
                        rangeWithDots.push(1);
                      }

                      rangeWithDots.push(...range);

                      if (currentPage + delta < totalPages - 1) {
                        rangeWithDots.push("...", totalPages);
                      } else {
                        if (totalPages > 1) {
                          rangeWithDots.push(totalPages);
                        }
                      }

                      return rangeWithDots;
                    };

                    return getPageNumbers().map((pageNumber, index) => {
                      if (pageNumber === "...") {
                        return (
                          <span key={index} className="px-2 text-gray-400">
                            ...
                          </span>
                        );
                      }

                      return (
                        <Button
                          key={pageNumber}
                          variant={
                            pageNumber === currentPage ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setCurrentPage(pageNumber)}
                          className="h-8 w-8 p-0"
                        >
                          {pageNumber}
                        </Button>
                      );
                    });
                  })()}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1"
                  >
                    Siguiente
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1"
                  >
                    Última
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
}
